/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */

import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';

import {
    useGetCompanySets,
    useUpdateCompanySetCompanies
} from '../../hooks/companySetHooks.js';

import RIQButton from '../../../../components/RIQButton.jsx';

import {
    RIQPopover,
    useRIQTooltip
} from '../../../../components/RIQPopover.jsx';


CompanySelector.propTypes = {
    selectedCompanySet: PropTypes.object,
    company: PropTypes.object.isRequired
};

function CompanySelector({company, selectedCompanySet}) {

    const {mutate: toggleCompanySelection, isLoading} = useUpdateCompanySetCompanies();
    const {data: companySets} = useGetCompanySets();

    const isClickable = !isLoading && selectedCompanySet;
    const appliedCompanySets = _(companySets)
        .filter(companySet => _.includes(companySet.companyIds, company.id))
        .sortBy(companySet => companySet.name.toLocaleLowerCase())
        .value();

    function onCheckboxChange(company) {
        if (!selectedCompanySet) {
            return;
        }

        const selectedCompanySetName = selectedCompanySet.name;

        const addCompanySetToCompany = !_.includes(selectedCompanySet.companyIds, company.id);

        toggleCompanySelection({
            companyIds: [company.id],
            companySetName: selectedCompanySetName,
            addCompanySetToCompany: addCompanySetToCompany
        });
    }

    return (
        <li
            key={company.id}
            className={`tlw-flex tlw-items-center tlw-border-0 tlw-border-b tlw-border-solid tlw-border-gray-300 tlw-px-7.5 tlw-py-5 ${isClickable ? 'tlw-cursor-pointer' : 'tlw-pointer-events-none tlw-cursor-not-allowed'}`}
            onClick={() => onCheckboxChange(company)}>
            {company.iconUrl &&
            <img
                className='tlw-h-10 tlw-w-10 tlw-overflow-hidden tlw-rounded-sm'
                src={company.iconUrl}
                alt={company.name} />}
            {!company.iconUrl &&
            <span className='tlw-h-10 tlw-w-10 tlw-border'>{company.name[0]}</span>}

            <div className='tlw-ml-10 tlw-flex tlw-grow tlw-flex-col'>
                <div className='tlw-flex tlw-items-center'>
                    {company.name}
                    {company.isFocusCompany &&
                        <span className='tlw-ml-5 tlw-rounded-sm tlw-bg-blue-400 tlw-px-2.5 tlw-py-1 tlw-text-xs tlw-font-bold tlw-text-white'>Focus Company</span>}

                    {appliedCompanySets.length > 0 &&
                        <RIQPopover
                            className='tlw-ml-2 tlw-cursor-pointer tlw-text-gray-500'
                            triggerNode={
                                <>
                                    <span className='fa fa-filter fa-color fa-fw' />
                                    <span className='tlw-text-sm'>{appliedCompanySets.length}</span>
                                </>
                            }
                            additionalTooltipProps={{
                                onClick: e => e.stopPropagation()
                            }}
                        >
                            <h3
                                className='tlw-m-0 tlw-px-7 tlw-py-4 tlw-text-md tlw-font-semibold tlw-text-gray-900'>
                                {company.name}
                            </h3>
                            <div
                                className='tlw-border-0 tlw-border-b tlw-border-t tlw-border-solid tlw-border-gray-300 tlw-bg-gray-150 tlw-p-4 tlw-text-sm tlw-text-gray-800'>
                                Company Sets
                            </div>

                            <div className='tlw-max-h-[200px] tlw-overflow-scroll'>
                                {appliedCompanySets.map(companySet => (
                                    <div
                                        key={companySet.id}
                                        className='tlw-border-0 tlw-border-b tlw-border-solid tlw-border-gray-300 tlw-px-2.5 tlw-py-4'>
                                        {companySet.name}
                                    </div>
                                ))}
                            </div>

                        </RIQPopover>
                    }
                </div>
                {company.isFocusCompany &&
                    <span className='tlw-text-sm tlw-text-gray-700'>Your focus company is always included in the analysis.</span>}
            </div>

            <div className='tlw-h-10 tlw-w-10'>
                <input
                    type='checkbox'
                    checked={_.includes(selectedCompanySet?.companyIds, company.id)}
                    disabled={!isClickable}
                    readOnly
                />
            </div>
        </li>
    );
}


CompaniesSelector.propTypes = {
    className: PropTypes.string,
    selectedCompanySetName: PropTypes.string,
    companies: PropTypes.array.isRequired,
    heightClass: PropTypes.string
};

export default function CompaniesSelector({className, selectedCompanySetName, companies, heightClass}) {

    const {data: companySets} = useGetCompanySets();
    const {mutate: toggleCompaniesSelection, isLoading} = useUpdateCompanySetCompanies();
    const {setTriggerRef, tooltipNode} = useRIQTooltip({
        text: RivalIQ.strings.company_set_modal_no_company_set_selected,
        placement: 'top',
        disabled: Boolean(selectedCompanySetName)
    });

    const selectedCompanySet = selectedCompanySetName && _.find(companySets, {
        name: selectedCompanySetName
    });

    const showSelectAll = !selectedCompanySet || _.size(selectedCompanySet.companyIds) < _.size(companies);

    function onToggleAllCompanies() {
        toggleCompaniesSelection({
            companyIds: _.map(companies, 'id'),
            companySetName: selectedCompanySetName,
            addCompanySetToCompany: showSelectAll
        });
    }

    let sortedCompanies = _.sortBy(companies, company => company.name.toLocaleLowerCase());
    const focusCompanyIdx = _.findIndex(sortedCompanies, 'isFocusCompany');
    if (focusCompanyIdx > -1) {
        const focusCompany = sortedCompanies.splice(focusCompanyIdx, 1);
        sortedCompanies = focusCompany.concat(sortedCompanies);
    }

    return (
        <div className={className}>
            <div className='tlw-flex tlw-items-center tlw-border tlw-border-l-0 tlw-border-r-0 tlw-border-t-0 tlw-border-solid tlw-border-gray-300 tlw-bg-gray-150 tlw-px-7.5 tlw-py-5 tlw-text-gray-800'>
                {selectedCompanySet &&
                <h5 className='tlw-m-0 tlw-grow tlw-overflow-hidden tlw-text-ellipsis tlw-whitespace-nowrap'>Select Companies for <strong>{selectedCompanySetName}</strong></h5>}
                {!selectedCompanySet &&
                <h5 className='tlw-m-0 tlw-grow'>Companies</h5>}

                <RIQButton
                    className='tlw-whitespace-nowrap tlw-px-2.5 tlw-py-0.5 tlw-text-sm tlw-leading-normal'
                    disabled={!selectedCompanySet || isLoading}
                    onClick={onToggleAllCompanies}>
                    {showSelectAll && 'Select All'}
                    {!showSelectAll && 'Deselect All'}
                </RIQButton>
            </div>

            <ul
                className={`tlw-m-0 tlw-overflow-auto tlw-p-0 ${heightClass} tlw-list-none ${!selectedCompanySet ? 'tlw-opacity-65' : ''}`}
                ref={setTriggerRef}>
                {sortedCompanies.map(company => (
                    <CompanySelector
                        key={company.id}
                        company={company}
                        selectedCompanySet={selectedCompanySet}
                    />
                ))}
            </ul>

            {tooltipNode}
        </div>
    );
}
